import React from 'react';
import { CreateScenarioDto, ScenarioStatus } from '../types';
import { ScenarioStatusBadge } from './ScenarioStatusBadge';

interface ScenarioPreviewProps {
  scenario: CreateScenarioDto & { status?: ScenarioStatus; id?: string };
}

export const ScenarioPreview: React.FC<ScenarioPreviewProps> = ({ scenario }) => {
  const persona = scenario.persona || {
    name: 'AI Persona',
    role: 'Customer',
    openingStatement: 'Hello, I need assistance.',
    startingMood: 'Neutral',
  };

  const personasList = Array.isArray(scenario.personas) && scenario.personas.length > 0
    ? scenario.personas
    : [persona];

  const rubrics = scenario.rubrics || {};
  const rubricKeys = Object.keys(rubrics);

  return (
    <div className="scenario-preview-card" style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', display: 'grid', gap: '20px' }}>
      {/* Header Banner */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--line)', paddingBottom: '18px' }}>
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', background: 'var(--blue-soft)', color: 'var(--blue)', padding: '3px 8px', borderRadius: '5px' }}>
              {scenario.category || 'General'} • {scenario.timeLimitMinutes || 10} MIN
            </span>
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', background: 'var(--surface)', color: 'var(--muted)', padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--line)' }}>
              {scenario.difficulty || 'INTERMEDIATE'}
            </span>
            {scenario.status && <ScenarioStatusBadge status={scenario.status} />}
          </div>

          <h2 style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 600, color: 'var(--navy)', margin: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {scenario.title || 'Untitled Simulation Scenario'}
          </h2>

          <p style={{ fontSize: '13.5px', color: 'var(--muted)', margin: '6px 0 0', lineHeight: 1.5, wordBreak: 'break-word' }}>
            {scenario.description || 'No description provided.'}
          </p>

          {/* Tags */}
          {scenario.tags && scenario.tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
              {scenario.tags.map((t) => (
                <span key={t} style={{ fontSize: '11px', background: '#F1F5F9', color: '#475569', padding: '2px 8px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                  🏷️ {t}
                </span>
              ))}
            </div>
          )}

          {/* Diagnostics and Hardware requirements */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>Device Diagnostics:</span>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: (scenario.requiresCamera !== false) ? 'var(--ok-soft)' : 'var(--surface)', color: (scenario.requiresCamera !== false) ? 'var(--ok)' : 'var(--muted)', border: '1px solid var(--line)' }}>
              📹 Camera: {(scenario.requiresCamera !== false) ? 'Required' : 'Optional'}
            </span>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: (scenario.requiresMicrophone !== false) ? 'var(--ok-soft)' : 'var(--surface)', color: (scenario.requiresMicrophone !== false) ? 'var(--ok)' : 'var(--muted)', border: '1px solid var(--line)' }}>
              🎙️ Mic: {(scenario.requiresMicrophone !== false) ? 'Required' : 'Optional'}
            </span>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: (scenario.requiresSpeaker !== false) ? 'var(--ok-soft)' : 'var(--surface)', color: (scenario.requiresSpeaker !== false) ? 'var(--ok)' : 'var(--muted)', border: '1px solid var(--line)' }}>
              🔊 Speaker: {(scenario.requiresSpeaker !== false) ? 'Required' : 'Optional'}
            </span>
          </div>
        </div>

        <div className="scenario-preview-languages" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Languages:</span>
            {(scenario.allowedLanguages || ['en', 'hi', 'ta']).map((lang) => (
              <span key={lang} style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', background: 'var(--surface)', border: '1px solid var(--line)', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>
                {lang}
              </span>
            ))}
          </div>
          {scenario.allowMixedLanguageReplies && (
            <span style={{ fontSize: '11px', color: '#4F46E5', background: '#EEF2FF', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
              🌐 Code-Switching & Hinglish Enabled
            </span>
          )}
        </div>
      </div>

      {/* Schedule, Attempts & Results Policy Overview Card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '12px', background: '#F8FAFC', padding: '14px 18px', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '12.5px' }}>
        <div>
          <span style={{ color: '#64748B', display: 'block' }}>Availability Schedule</span>
          <strong>{scenario.startsAt ? new Date(scenario.startsAt).toLocaleDateString() : 'Immediate'}</strong> to <strong>{scenario.dueAt ? new Date(scenario.dueAt).toLocaleDateString() : 'No Deadline'}</strong>
        </div>
        <div>
          <span style={{ color: '#64748B', display: 'block' }}>Attempts & Pacing</span>
          <strong>{scenario.maxAttempts || 3} Max Retries</strong> ({(scenario.attemptGapHours ?? 0) > 0 ? `${scenario.attemptGapHours}h Cool-off` : 'Immediate retry'})
        </div>
        <div>
          <span style={{ color: '#64748B', display: 'block' }}>Time-Out Action</span>
          <strong>{scenario.timeOutAction === 'AUTO_SUBMIT' ? 'Hard Auto-Submit' : scenario.timeOutAction === 'EXTEND_GRACE' ? '2-Min Grace Wrap-up' : 'Warn at 1 Min Left'}</strong>
        </div>
        <div>
          <span style={{ color: '#64748B', display: 'block' }}>Results & Score Policy</span>
          <strong>{scenario.releaseResultsTiming || 'IMMEDIATELY'}</strong> • <strong>{scenario.scoreCountingPolicy || 'BEST'} Score</strong>
        </div>
      </div>

      {/* Grid: Character Dossier & Key Objectives */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '16px' }}>
        {/* Character Dossier (Supports Multi-Persona) */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '16px', display: 'grid', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--blue)' }}>
              AI Counterparts ({personasList.length})
            </span>
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              Opener: <strong style={{ color: 'var(--warn)' }}>{personasList[0]?.name || 'Primary Persona'}</strong>
            </span>
          </div>

          {personasList.map((p, pIdx) => (
            <div key={p.id || pIdx} style={{ background: 'var(--white)', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px', display: 'grid', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '13px', color: '#1E293B' }}>
                  {p.name} <span style={{ fontWeight: 400, color: '#64748B' }}>({p.role})</span>
                </strong>
                <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: '#FEF3C7', color: '#92400E', fontWeight: 600 }}>
                  Mood: {p.startingMood || 'Neutral'}
                </span>
              </div>

              {p.openingStatement && (
                <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#334155', background: '#F8FAFC', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                  "{p.openingStatement}"
                </div>
              )}

              {p.objections && p.objections.length > 0 && (
                <div style={{ fontSize: '11.5px', color: '#64748B', marginTop: '2px' }}>
                  <strong>Key Objections ({p.objections.length}): </strong>
                  {p.objections.slice(0, 2).join(' • ')}
                  {p.objections.length > 2 ? ` (+${p.objections.length - 2} more)` : ''}
                </div>
              )}
            </div>
          ))}

          <div style={{ fontSize: '12.5px', color: 'var(--ink)', borderTop: '1px solid var(--line)', paddingTop: '8px' }}>
            <span style={{ color: 'var(--muted)' }}>Learner Acts As: </span>
            <strong>{scenario.learnerRole || 'Representative'}</strong>
            {scenario.learnerObjective && (
              <div style={{ marginTop: '4px', color: '#1E293B' }}>
                <strong>Learner Mission: </strong>{scenario.learnerObjective}
              </div>
            )}
          </div>
        </div>

        {/* Key Objectives, Guidelines & Company Policies */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '16px', display: 'grid', gap: '12px' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ok)' }}>
            Key Objectives & Policies
          </span>

          {Array.isArray(scenario.objectives) && scenario.objectives.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--ink)', display: 'grid', gap: '6px' }}>
              {scenario.objectives.map((obj, i) => (
                <li key={obj.id || i}>
                  <span style={{ fontWeight: 500 }}>{obj.title}</span>
                  {obj.required && <span style={{ color: 'var(--bad)', marginLeft: '4px', fontSize: '12px' }}>*</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ fontSize: '12.5px', color: 'var(--muted)', margin: 0, fontStyle: 'italic' }}>No specific objectives configured.</p>
          )}

          {/* Company Policies */}
          {scenario.companyPolicies && scenario.companyPolicies.length > 0 && (
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: '8px', display: 'grid', gap: '4px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--navy)' }}>⚖️ Enforced Policies:</span>
              <div style={{ display: 'grid', gap: '4px' }}>
                {scenario.companyPolicies.map((pol, idx) => (
                  <div key={idx} style={{ fontSize: '12px', color: '#92400E', background: '#FEF3C7', padding: '4px 8px', borderRadius: '4px', border: '1px solid #FDE68A' }}>
                    • {pol}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reference Documents */}
          {scenario.referenceDocuments && scenario.referenceDocuments.length > 0 && (
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: '8px', display: 'grid', gap: '4px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--navy)' }}>📄 Reference Documents:</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {scenario.referenceDocuments.map((doc, idx) => (
                  <span key={idx} style={{ fontSize: '11.5px', color: '#1E40AF', background: '#EFF6FF', padding: '2px 8px', borderRadius: '4px', border: '1px solid #BFDBFE' }}>
                    📎 {doc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Competencies & Evaluation Rubrics Summary */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '16px' }}>
        <h4 style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', margin: '0 0 12px' }}>
          Evaluation Competency Rubric
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '10px' }}>
          {rubricKeys.length === 0 ? (
            <p style={{ fontSize: '12.5px', color: 'var(--muted)', margin: 0 }}>No competencies specified.</p>
          ) : (
            rubricKeys.map((k) => {
              const r = rubrics[k];
              const displayWeight = r.weight <= 1.0 ? Math.round(r.weight * 100) : Math.round(r.weight);
              const isCritical = Boolean(r.critical);
              return (
                <div key={k} style={{ background: 'var(--white)', border: isCritical ? '1.5px solid #FCA5A5' : '1px solid var(--line)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', wordBreak: 'break-word' }}>{r.name}</span>
                      {isCritical && (
                        <span style={{ fontSize: '10px', background: '#FEE2E2', color: '#B91C1C', padding: '1px 5px', borderRadius: '4px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          CRITICAL GATE
                        </span>
                      )}
                    </div>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '12px', color: 'var(--blue)' }}>{displayWeight}%</span>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: 1.4, wordBreak: 'break-word' }}>{r.description}</p>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Footer Preview Note */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', borderTop: '1px solid var(--line)', paddingTop: '14px', fontSize: '12.5px', color: 'var(--muted)' }}>
        <span style={{ wordBreak: 'break-word' }}>Target Passing Score: <strong>{scenario.passScore || 70}%</strong> • Max Attempts: <strong>{scenario.maxAttempts || 3}</strong></span>
        <span style={{ background: 'var(--blue-soft)', color: 'var(--blue)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>Studio Preview</span>
      </div>
    </div>
  );
};
